import { equipmentListProps } from '@cat-ecom/pcc-components/src/components/my-equipment/types';
import { AddEquipmentItem, Equipment } from 'services/equipment/types';
import type { SelectedEquipmentInfo as SelectedEquipmentInfoType } from 'app/[lang]/[esite]/(core)/_header/components/equipment-widget/types';
import {
  ITEMS_PER_PAGE,
  MY_EQUIPMENT_SEARCH_KEYS,
  SEARCH_KEYS
} from './constants';

export const isEquipmentInUserList = (
  myEquipmentList: Equipment[],
  equipmentToSearch: AddEquipmentItem
): Equipment[] | [] => {
  const serialNumberToSearch = equipmentToSearch.serialNumber
    ? equipmentToSearch.serialNumber.toUpperCase()
    : '';
  const equipmentInUserList = [];
  const foundEquipmentInList = myEquipmentList?.find(
    (equipment: Equipment) =>
      (equipment?.id === equipmentToSearch?.productId ||
        equipment.partNumber === equipmentToSearch.partNumber) &&
      equipment.serialNumber === serialNumberToSearch
  );
  if (foundEquipmentInList) {
    equipmentInUserList.push(foundEquipmentInList);
  }
  return equipmentInUserList;
};

const formatSelectedEquipmentInfo: (
  selectedEquipmentInfo: SelectedEquipmentInfoType
) => equipmentListProps = (selectedEquipmentInfo) => {
  const formattedSelectedEquipmentInfo: equipmentListProps = {
    assetId: selectedEquipmentInfo.assetId ?? '',
    dcn: null,
    dcnName: '',
    dealerCode: '',
    dealerMakeCode: '',
    dealerName: '',
    equipmentFamily:
      'productFamily' in selectedEquipmentInfo
        ? selectedEquipmentInfo.productFamily
        : '',
    equipmentUniqueId: '',
    imageURL: selectedEquipmentInfo?.imageURL || '',
    isQRCodeTempEquipment: false,
    model: selectedEquipmentInfo?.model,
    multiple: false,
    name: selectedEquipmentInfo?.name || '',
    productId: selectedEquipmentInfo?.productId || '',
    seoUrl: selectedEquipmentInfo?.seoUrl || '',
    serialNumber: selectedEquipmentInfo?.serialNumber || '',
    ucid: '',
    ucidName: '',
    unValidatedEquipmentId: '',
    unValidatedItemListId: selectedEquipmentInfo?.unvalidatedItemListId || '',
    uniqueIndex: selectedEquipmentInfo?.uniqueIndex || '',
    validSerialAndModel: false,
    validatedAsset: false
  };
  return formattedSelectedEquipmentInfo;
};

export const formatToMyEquipmentList: (
  equipment: Equipment
) => equipmentListProps = (equipment) => {
  const formattedEquipment: equipmentListProps = {
    assetId: equipment.assetName ?? '',
    dcn: equipment?.dcn || null,
    dcnName: '',
    dealerCode: '',
    dealerMakeCode: '',
    dealerName: '',
    equipmentFamily:
      'productFamily' in equipment ? equipment.productFamily : '',
    equipmentUniqueId: '',
    imageURL: equipment?.imageURL || '',
    isQRCodeTempEquipment: false,
    model: equipment?.model,
    multiple: false,
    name: equipment?.name || '',
    productId: equipment?.id || '',
    seoUrl: equipment.seoUrl || '',
    id: equipment.id || '',
    serialNumber: equipment?.serialNumber || '',
    ucid: '',
    ucidName: '',
    unValidatedEquipmentId: equipment?.unvalidatedEquipmentId || '',
    unValidatedItemListId: equipment?.unvalidatedItemListId || '',
    uniqueIndex: equipment?.uniqueIndex || '',
    validSerialAndModel: equipment?.isSerialAndModelValid || false,
    validatedAsset: equipment?.isValidatedAsset || false
  };
  return formattedEquipment;
};

const formatEquipmentList: (
  equipmentList: Equipment[]
) => equipmentListProps[] = (equipmentList) => {
  return equipmentList.map((equipment: Equipment) => {
    return formatToMyEquipmentList(equipment);
  });
};
export const sortEquipmentsBasedOnDCNandValidation = (
  equipmentList: equipmentListProps[],
  dcn: string | null
): equipmentListProps[] => {
  const unValidatedEquipmentsWithActiveDCN = equipmentList.filter((obj) => {
    return obj.validatedAsset === false && obj.dcn !== null && obj.dcn === dcn;
  });
  const unValidatedEquipmentsWithOtherDCN = equipmentList.filter((obj) => {
    return obj.validatedAsset === false && obj.dcn !== null && obj.dcn !== dcn;
  });
  const unValidatedEquipmentsWithNoDCN = equipmentList.filter((obj) => {
    return obj.validatedAsset === false && obj.dcn === null;
  });
  const validatedEquipmentsWithActiveDCN = equipmentList.filter((obj) => {
    return obj.validatedAsset === true && obj.dcn !== null && obj.dcn === dcn;
  });
  const validatedEquipmentsWithOtherDCN = equipmentList.filter((obj) => {
    return obj.validatedAsset === true && obj.dcn !== null && obj.dcn !== dcn;
  });
  const validatedEquipmentsWithNoDCN = equipmentList.filter((obj) => {
    return obj.validatedAsset === true && obj.dcn === null;
  });
  const sortedList = unValidatedEquipmentsWithActiveDCN.concat(
    unValidatedEquipmentsWithOtherDCN,
    unValidatedEquipmentsWithNoDCN,
    validatedEquipmentsWithActiveDCN,
    validatedEquipmentsWithOtherDCN,
    validatedEquipmentsWithNoDCN
  );
  return sortedList;
};

const sortEquipmentList = (
  equipmentList: equipmentListProps[],
  selectedEquipmentIndex: number,
  dcn: string | null
): equipmentListProps[] => {
  const selectedEquipment: equipmentListProps[] =
    selectedEquipmentIndex > -1
      ? equipmentList.splice(selectedEquipmentIndex, 1)
      : [];
  const sortedEquipmentList =
    equipmentList.length > 1
      ? sortEquipmentsBasedOnDCNandValidation(equipmentList, dcn)
      : equipmentList;
  if (selectedEquipment.length !== 0) {
    sortedEquipmentList.unshift(selectedEquipment[0]);
  }
  return sortedEquipmentList;
};

const getFilteredList = (
  list: equipmentListProps[] | Equipment[],
  searchString: string,
  searchKey = SEARCH_KEYS
) => {
  return list.filter((obj) => {
    return Object.keys(obj).some((key) => {
      if (searchKey.includes(key)) {
        return obj[key]?.toLowerCase().includes(searchString.toLowerCase());
      }
    });
  });
};

const equipmentListOrderChange = (equipments: Equipment[]) => {
  return equipments.sort(
    (verifiedEquipment, nonVerifiedEquipment) =>
      Number(verifiedEquipment.isValidatedAsset) -
      Number(nonVerifiedEquipment.isValidatedAsset)
  );
};

const equipmentListFilterWithSearch = (
  equipments: Equipment[],
  selectedIndex: number,
  searchTerms: string
) => {
  const filteredEquipments = equipments.filter((equipment) => {
    if (searchTerms) {
      return searchFiltered(equipment, searchTerms);
    } else {
      return true;
    }
  });

  if (selectedIndex >= 0 && selectedIndex < filteredEquipments.length) {
    const selectedEquipment = filteredEquipments[selectedIndex];
    filteredEquipments.splice(selectedIndex, 1); // Remove selected equipment from the array
    return [selectedEquipment, ...equipmentListOrderChange(filteredEquipments)];
  } else {
    return equipmentListOrderChange(filteredEquipments);
  }
};

const searchFiltered = (equipment: Equipment, searchString: string) => {
  if (!searchString) {
    return true;
  }
  const filteredEquipment = getFilteredList(
    [equipment],
    searchString,
    MY_EQUIPMENT_SEARCH_KEYS
  )?.[0];

  return Object.keys(filteredEquipment || {})?.length > 0;
};

const paginatedEquipments = (equipments: Equipment[], currentPage: number) => {
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const totalPageCount = equipments?.length || 0;
  const paginatedEquipmentList = equipments?.slice(startIndex, endIndex);
  return { paginatedEquipmentList, totalPageCount };
};

const getFamilyName = (model: string, name: string) => {
  return name?.replace(model, '')?.trim() ?? '';
};

const searchPagination = (
  equipments: Equipment[],
  selectedIndex: number,
  searchTerms: string,
  currentPage: number
) => {
  const filteredEquipments = equipmentListFilterWithSearch(
    equipments,
    selectedIndex,
    searchTerms
  );
  return paginatedEquipments(filteredEquipments, currentPage);
};
const getEquipmentsData = (
  equipmentList: Equipment[],
  selectedEquipmentIndex: number
) => {
  const hasEquipmentToSelect = (equipmentList || [])?.length > 0;

  const {
    serialNumber,
    assetName,
    imageURL,
    model,
    productFamily,
    unvalidatedItemListId,
    uniqueIndex,
    id,
    partNumber,
    isValidatedAsset,
    seoUrl
  } = (hasEquipmentToSelect && equipmentList[selectedEquipmentIndex]) || {};

  return {
    serialNumber,
    assetName,
    imageURL,
    model,
    productFamily,
    unvalidatedItemListId,
    uniqueIndex,
    id,
    partNumber,
    isValidatedAsset,
    seoUrl
  };
};

export {
  formatSelectedEquipmentInfo,
  formatEquipmentList,
  sortEquipmentList,
  getFilteredList,
  equipmentListFilterWithSearch,
  searchFiltered,
  getFamilyName,
  searchPagination,
  paginatedEquipments,
  getEquipmentsData
};
